<template>
  <div
    class="rounded-lg border bg-white p-2 sm:p-3"
    :class="{
      'bg-gray-100': isRemoved,
      'border-lk-green': isModified,
    }"
  >
    <div
      class="flex items-center space-x-2 sm:space-x-3"
      :class="{ 'opacity-50': isRemoved }"
    >
      <img
        :src="product.mainImageUrl"
        class="size-8 shrink-0 rounded object-cover sm:size-10 md:size-12"
      />
      <div class="flex-1">
        <h4
          class="text-xs font-medium sm:text-sm"
          :class="{ 'line-through': isRemoved }"
        >
          {{ product.productName }}
        </h4>
        <p class="text-xs text-gray-500">{{ product.productId }}</p>
        <p class="text-xs sm:text-sm">
          <template v-if="isModified">
            <span class="text-gray-500">Cantidad anterior: {{ product.quantity }}</span>
            <br />
            <span class="font-medium"> Cantidad nueva: {{ productModification!.newQuantity }} </span>
          </template>
          <template v-else>
            Cantidad actual:
            <span :class="{ 'line-through': isRemoved }">
              {{ productModification?.newQuantity ?? product.quantity }}
            </span>
          </template>
        </p>
      </div>
      <div class="flex flex-col space-y-1 sm:space-y-2">
        <template v-if="!isRemoved">
          <button
            type="button"
            class="rounded bg-blue-100 p-2 text-xs text-blue-700 hover:bg-blue-200 sm:px-3 sm:py-1"
            @click="startEditing"
          >
            Modificar cantidad
          </button>
          <button
            type="button"
            class="rounded bg-red-100 p-2 text-xs text-red-700 hover:bg-red-200 sm:px-3 sm:py-1"
            @click="markAsOutOfStock"
          >
            Sin stock
          </button>
        </template>
        <template v-else>
          <span class="text-xs font-medium text-red-600">Sin stock</span>
          <button
            type="button"
            class="rounded bg-gray-900 p-2 text-xs text-white hover:bg-gray-800 sm:px-3 sm:py-1"
            @click="restore"
          >
            Deshacer
          </button>
        </template>
      </div>
    </div>
    <div
      v-if="isEditing"
      class="mt-2 flex flex-col space-y-2 border-t pt-2 sm:mt-3 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0 sm:pt-3"
    >
      <label class="text-xs font-medium sm:text-sm">Nueva cantidad:</label>
      <input
        v-model.number="tempQuantity"
        type="number"
        min="1"
        class="w-16 rounded border border-gray-300 p-2 text-center text-xs focus:border-lk-green focus:outline-none sm:w-20 sm:py-1 sm:text-sm"
      />
      <button
        type="button"
        class="rounded border border-lk-green bg-white px-3 py-2 text-xs font-medium text-lk-green hover:bg-lk-green hover:text-white sm:py-1"
        @click="saveQuantity"
      >
        Guardar
      </button>
      <button
        type="button"
        class="rounded bg-gray-200 px-3 py-2 text-xs text-gray-700 hover:bg-gray-300 sm:py-1"
        @click="cancelEdit"
      >
        Cancelar
      </button>
    </div>
    <div
      v-if="isRemoved"
      class="mt-2 rounded-md bg-white p-2 sm:mt-3 sm:p-3"
    >
      <label class="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">Tiempo de reposición:</label>
      <lokal-generic-combobox
        :model-value="selectedReplacementTime"
        class="w-full text-xs"
        :options="replacementTimeOptions"
        :get-option-label="(option) => option.label"
        :get-option-key="(option) => option.key"
        :options-label="'Seleccionar tiempo'"
        :multiple="false"
        :local-search="false"
        :compare-by="'key'"
        @update:model-value="updateReplacementTime"
      >
        <template
          v-for="option in replacementTimeOptions"
          :key="option.key"
          #[`custom-option-${option.key}`]="{ active, selected }"
        >
          <li
            class="flex cursor-pointer items-center p-1 text-xs"
            :class="{ 'bg-lk-green text-white': active || selected }"
          >
            <span class="text-xs">{{ option.label }}</span>
          </li>
        </template>
      </lokal-generic-combobox>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import lokalGenericCombobox from './shared/lokal-generic-combobox.vue';

const replacementTimeOptions = [
  { key: 1, label: 'Menos de 3 días' },
  { key: 2, label: 'Entre 3 y 7 días' },
  { key: 3, label: 'Entre 7 y 14 días' },
  { key: 4, label: 'Sin fecha estimada' },
];
const DEFAULT_REPLACEMENT_TIME = replacementTimeOptions[3];

interface ProductAdjustmentCardProps {
  product: SubOrderProduct;
  productModification?: ProductModification;
}

interface ProductAdjustmentCardEmits {
  'update:product-modification': [productId: number, modification: ProductModification | null];
}

const props = defineProps<ProductAdjustmentCardProps>();
const emit = defineEmits<ProductAdjustmentCardEmits>();

const isEditing = ref(false);
const tempQuantity = ref(0);

const isRemoved = computed(() => props.productModification?.newQuantity === 0);
const isModified = computed(() => props.productModification && !isRemoved.value);

const selectedReplacementTime = computed(() => {
  if (!props.productModification?.replacementTime) return DEFAULT_REPLACEMENT_TIME;
  return (
    replacementTimeOptions.find((option) => option.label === props.productModification?.replacementTime) ||
    DEFAULT_REPLACEMENT_TIME
  );
});

function startEditing() {
  isEditing.value = true;
  tempQuantity.value = props.productModification?.newQuantity ?? props.product.quantity;
}

function saveQuantity() {
  if (tempQuantity.value !== props.product.quantity) {
    emit('update:product-modification', props.product.id, {
      subOrderProduct: props.product,
      originalQuantity: props.product.quantity,
      newQuantity: tempQuantity.value,
      replacementTime: tempQuantity.value === 0 ? DEFAULT_REPLACEMENT_TIME.label : '',
    });
  } else {
    emit('update:product-modification', props.product.id, null);
  }
  cancelEdit();
}

function cancelEdit() {
  isEditing.value = false;
  tempQuantity.value = 0;
}

function markAsOutOfStock() {
  emit('update:product-modification', props.product.id, {
    subOrderProduct: props.product,
    originalQuantity: props.product.quantity,
    newQuantity: 0,
    replacementTime: DEFAULT_REPLACEMENT_TIME.label,
  });
}

function restore() {
  emit('update:product-modification', props.product.id, null);
}

function updateReplacementTime(selectedValue: { key: number; label: string }) {
  if (props.productModification) {
    emit('update:product-modification', props.product.id, {
      ...props.productModification,
      replacementTime: selectedValue.label,
    });
  }
}
</script>
